// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useState } from 'react';
import axios from 'axios';
import { Button, TextArea, useToast } from '@loa/ui-components';
import { proposalApi } from '../../../../../api/proposalApi';
import type { BreakthroughCoverSectionContent, ProposalSection } from '../../../../../types/proposal';

export type SectionCoverProps = Readonly<{
  proposalId: string;
  section: ProposalSection;
  onSaved: (updated: ProposalSection) => void;
  /**
   * Renders the "Client view" preview (BR-P5, PRD §5 / RQ-BR8): the meta
   * facts card is unchanged (already read-only), but the report-intro
   * editor + Save button are replaced with a plain disabled display of
   * whatever has been saved. Defaults to false (advisor editable builder).
   */
  readOnly?: boolean;
}>;

function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { message?: string } | undefined)?.message ?? fallback;
  }
  return fallback;
}

function readContent(section: ProposalSection): BreakthroughCoverSectionContent {
  return (section.content as BreakthroughCoverSectionContent | null) ?? {};
}

/**
 * BR_COVER (PRD §4 row 1) — a read-only meta card (title, client, advisor,
 * brokerage, date) seeded entirely at builder creation, plus an editable
 * "report intro" the advisor writes for the client. There is no server
 * resolver for this key, so the whole content object round-trips verbatim
 * through `updateSection`.
 */
export function SectionCover({ proposalId, section, onSaved, readOnly = false }: SectionCoverProps) {
  const { showToast } = useToast();
  const content = readContent(section);
  const [reportIntro, setReportIntro] = useState(content.reportIntro ?? '');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = useCallback((value: string) => {
    setReportIntro(value);
    setDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const updated = await proposalApi.updateSection(proposalId, section.sectionKey, {
        content: { ...content, reportIntro },
      });
      setDirty(false);
      onSaved(updated);
      showToast('Cover saved', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to save Cover'), 'error');
    } finally {
      setSaving(false);
    }
  }, [saving, content, reportIntro, proposalId, section.sectionKey, onSaved, showToast]);

  return (
    <div className="pension-builder__section" data-testid="section-br-cover">
      <div className="pension-builder__facts-card" data-testid="section-br-cover-meta">
        <h3 className="pension-builder__facts-card-title">{content.title || 'Your Financial Plan'}</h3>
        <dl className="pension-builder__facts-grid">
          <Fact label="Client" value={content.clientName || '—'} />
          <Fact label="Advisor" value={content.advisorName || '—'} />
          <Fact label="Brokerage" value={content.brokerageName || '—'} />
          <Fact label="Date" value={content.date || '—'} />
        </dl>
      </div>

      {readOnly ? (
        reportIntro && (
          <TextArea
            label="Report intro"
            value={reportIntro}
            disabled
            readOnly
            rows={4}
            fluid
            data-testid="section-br-cover-intro"
          />
        )
      ) : (
        <>
          <TextArea
            label="Report intro"
            value={reportIntro}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="A short welcome note shown at the start of the report…"
            rows={4}
            fluid
            data-testid="section-br-cover-intro"
          />

          <div className="pension-builder__section-footer">
            <Button
              variant="filled"
              onClick={handleSave}
              disabled={saving || !dirty}
              loading={saving}
              data-testid="section-br-cover-save-btn"
            >
              Save
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function Fact({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="pension-builder__fact">
      <dt className="pension-builder__fact-label">{label}</dt>
      <dd className="pension-builder__fact-value">{value}</dd>
    </div>
  );
}

export default SectionCover;
